/**
 * Thin client for the Keystone Worker API (apps/server, docs/design/multiplayer.md).
 *
 *   const api = new ApiClient();                 // same origin; token in localStorage
 *   if (await api.available()) await api.ensureDevice();
 *   const { profile, version } = await api.getProfile();
 *
 * Every call returns the parsed JSON body plus `status`; network failures throw ApiOffline so
 * callers can drop to local mode. The device token is the only secret kept client-side.
 */
import type { StorageLike } from './profileStore';

export const DEVICE_TOKEN_KEY = 'keystone.device';

export class ApiOffline extends Error {}

export type ApiReply<T = Record<string, unknown>> = T & { status: number; error?: string; reason?: string };

export interface ProfileReply {
  /** progression serialize() output with the seed hidden; null when the player has none yet. */
  profile: string | null;
  version: number;
  imported?: boolean;
}

export interface ActReply extends ProfileReply {
  result?: Record<string, unknown>;
}

export class ApiClient {
  token: string | null;

  constructor(
    private readonly base = '',
    private readonly storage: StorageLike | null = safeStorage(),
    private readonly fetchFn: typeof fetch = (...a) => fetch(...a),
  ) {
    let t: string | null = null;
    try {
      t = storage?.getItem(DEVICE_TOKEN_KEY) ?? null;
    } catch {
      t = null;
    }
    this.token = t;
  }

  /** True when /api/health answers like our Worker (the Vite dev server answers HTML). */
  async available(timeoutMs = 2500): Promise<boolean> {
    try {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), timeoutMs);
      const r = await this.fetchFn(`${this.base}/api/health`, { signal: ctl.signal, cache: 'no-store' });
      clearTimeout(timer);
      if (!r.ok) return false;
      const j = (await r.json()) as { ok?: unknown };
      return j.ok === true;
    } catch {
      return false;
    }
  }

  /** Make sure we hold a device token (creates an anonymous player on first use). */
  async ensureDevice(): Promise<string> {
    if (this.token) return this.token;
    const r = await this.call<{ token?: string }>('POST', '/api/device', undefined, false);
    if (!r.token) throw new ApiOffline(`device registration failed (${r.status})`);
    this.token = r.token;
    try {
      this.storage?.setItem(DEVICE_TOKEN_KEY, r.token);
    } catch {
      /* the token lives for this tab only */
    }
    return r.token;
  }

  /** Forget the token (a 401: the server no longer knows this device). */
  forgetDevice(): void {
    this.token = null;
    try {
      this.storage?.removeItem(DEVICE_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }

  getProfile(): Promise<ApiReply<ProfileReply>> {
    return this.call('GET', '/api/profile');
  }

  createProfile(mainClassId: string): Promise<ApiReply<ProfileReply>> {
    return this.call('POST', '/api/profile/create', { mainClassId });
  }

  importProfile(json: string): Promise<ApiReply<ProfileReply>> {
    return this.call('POST', '/api/profile/import', { json });
  }

  act(op: string, args: unknown, base: number, localDate: string): Promise<ApiReply<ActReply>> {
    return this.call('POST', '/api/profile/act', { op, args, base, localDate });
  }

  runStart(body: { dungeonId: string; level: number; spectate: boolean }): Promise<ApiReply<{ ticket?: string; seed?: number; level?: number; dungeonId?: string }>> {
    return this.call('POST', '/api/run/start', body);
  }

  runFinish(body: Record<string, unknown>): Promise<ApiReply<ProfileReply & { summary?: unknown; claims?: unknown; result?: unknown }>> {
    return this.call('POST', '/api/run/finish', body);
  }

  /**
   * WebSocket URL of a party room (the token rides in the query: browsers can't set headers).
   * `join`: the party must already exist (invite links, typed codes); otherwise it is created.
   */
  partySocketUrl(code: string, join = false): string {
    const loc = (globalThis as { location?: Location }).location;
    const origin = this.base || (loc ? loc.origin : '');
    return `${origin.replace(/^http/, 'ws')}/api/party/${encodeURIComponent(code)}/ws?token=${encodeURIComponent(this.token ?? '')}${join ? '&join=1' : ''}`;
  }

  private async call<T>(method: 'GET' | 'POST', path: string, body?: unknown, auth = true): Promise<ApiReply<T>> {
    let r: Response;
    try {
      r = await this.fetchFn(`${this.base}${path}`, {
        method,
        cache: 'no-store',
        headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(auth && this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new ApiOffline(err instanceof Error ? err.message : String(err));
    }
    let j: Record<string, unknown> = {};
    try {
      j = (await r.json()) as Record<string, unknown>;
    } catch {
      if (r.status >= 500 || r.status === 0) throw new ApiOffline(`HTTP ${r.status}`);
    }
    if (r.status >= 500) throw new ApiOffline(String(j['error'] ?? `HTTP ${r.status}`));
    return { ...(j as T), status: r.status } as ApiReply<T>;
  }
}

function safeStorage(): StorageLike | null {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    return null;
  }
}
